import React from 'react'
import { X } from 'lucide-react'
import { cn } from '../UI/cn'
import type { EnrichedAction } from '../../services/recommendations_contracts'

/**
 * Tagged union describing what (if anything) the homepage right panel
 * is currently showing. Single-panel-at-a-time — setting a new kind
 * replaces whatever was open. ``closed`` is the resting state.
 *
 * Add new kinds here as the right panel hosts more workflows.
 */
export type RightPanelState =
  | { kind: 'closed' }
  | { kind: 'recently-completed' }
  | { kind: 'apply-rec'; action: EnrichedAction }

interface RightPanelProps {
  /** Top-of-panel content (icon + title text). The close button is
   *  rendered automatically on the right of the header — callers
   *  shouldn't include their own. */
  header: React.ReactNode
  /** Optional sub-header content rendered between the main header strip
   *  and the scrolling body. Use this for context that should always be
   *  visible (entity chips, recommendation summary, tab strip) instead
   *  of relying on ``position: sticky`` inside the scroll body — sticky
   *  fights with the body's ``mask-image`` for the bottom-fade effect
   *  and lets descendant stacking contexts (compose panels, react-
   *  selects) bleed through. Sub-header lives outside the scroll
   *  container, so nothing inside the body can paint over it. */
  subHeader?: React.ReactNode
  /** Scrollable body content. Sized to fill the space between the
   *  header (+ optional sub-header) and (optional) footer. */
  children: React.ReactNode
  /** Optional pinned-to-bottom action footer. When omitted the body
   *  scrolls to the panel's bottom edge directly. */
  footer?: React.ReactNode
  /** Fired when the user clicks the close button. */
  onClose: () => void
}

/**
 * Generic right-panel chrome for the homepage sider host. Provides:
 *
 * - Header strip — fixed at top, ``--header-height`` tall, matches the
 *   global nav so the two strips read as a single horizontal band.
 *   Caller supplies the inner content (icon + title); the close button
 *   is rendered here so every panel handles dismissal consistently.
 * - Body — fills the remaining vertical space, scrolls independently.
 * - Footer — optional. When provided, sits pinned at the bottom; the
 *   body scrolls "behind" it via a soft mask-image gradient + matching
 *   bottom padding so the last few px of content fade beneath the
 *   footer instead of being hard-clipped.
 *
 * Layout-only — the panel doesn't know what kind of content it holds.
 * Different panel "kinds" supply their own header/body/footer.
 */
export const RightPanel: React.FC<RightPanelProps> = ({
  header,
  subHeader,
  children,
  footer,
  onClose,
}) => {
  // Bottom padding + mask gradient on the body so content fades into
  // transparency behind the footer instead of being abruptly cut.
  // Keep these two values in sync — pb provides the scrollable buffer,
  // mask blends the visual transition over the same range.
  //
  // ``pt`` is conditional: when a subHeader is present it provides the
  // breathing room above the scroll body, so the body itself starts
  // flush. Otherwise the body adds its own pt-4 so content doesn't
  // crash into the panel header strip.
  const bodyClasses = cn(
    'flex-1 overflow-y-auto px-12',
    subHeader ? 'pt-0' : 'pt-4',
    footer
      ? 'pb-20 [mask-image:linear-gradient(to_top,transparent_0,black_64px)]'
      : 'pb-4'
  )

  return (
    <div className="w-full h-full flex flex-col bg-surface relative">
      <div className="flex items-center justify-between gap-2 px-12 py-3 min-h-[var(--header-height)] border-b border-line flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0 flex-1">{header}</div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close panel"
          className="p-1.5 rounded text-fg-muted hover:text-fg hover:bg-surface-alt transition-colors flex-shrink-0"
        >
          <X size={18} />
        </button>
      </div>
      {subHeader && (
        <div className="px-12 pt-4 pb-3 flex-shrink-0">{subHeader}</div>
      )}
      <div className={bodyClasses}>{children}</div>
      {footer && (
        <div className="absolute bottom-0 left-0 right-0 flex items-center justify-end gap-2 px-12 py-3 border-t border-line bg-surface flex-shrink-0">
          {footer}
        </div>
      )}
    </div>
  )
}
